import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DataConsumptionStateService } from '@modules/paywall/data-consumption/data-consumption-state.service';
import { AnimateFadeDirective } from '@modules/shared/directives/animate-fade.directive';
import { Store } from '@ngrx/store';
import { ClientDataConsumption } from '@pixacare/domain';
import { ContactUsService } from '@services/contact-us.service';
import { DialogService } from '@services/dialog.service';
import { StorageService } from '@services/storage.service';
import { DataConsumptionState } from '@shared/models/paywall/data-consumption-state';
import { FormatGoDataUnitPipe } from '@shared/pipes/format-go-data-unit.pipe';
import { selectClientDataConsumptionInGb } from '@shared/store/clients/clients.selectors';
import { selectClientCode } from '@shared/store/router/router.selectors';
import { TuiButton, TuiDialogContext, TuiHint, TuiIcon, TuiTitle } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { map, Observable, switchMap } from 'rxjs';

@Component({
  selector: 'pxc-data-consumption',
  templateUrl: './data-consumption.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TuiIcon,
    FormatGoDataUnitPipe,
    AnimateFadeDirective,
    TuiHint,
    TuiButton,
    TuiTitle,
    TranslocoDirective,
  ],
  providers: [DataConsumptionStateService],
})
export class DataConsumptionComponent {
  private readonly store = inject(Store);
  private readonly storageService = inject(StorageService);
  private readonly dialogService = inject(DialogService);
  private readonly context = inject<
    TuiDialogContext<
      void,
      {
        collapsed?: boolean;
        detailedMode?: boolean;
      }
    >
  >(POLYMORPHEUS_CONTEXT, { optional: true });
  private readonly contactUsService = inject(ContactUsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dataConsumptionStateService = inject(DataConsumptionStateService);

  @Input() collapsed = this.context?.data.collapsed || false;
  @Input() detailedMode = this.context?.data.detailedMode || false;

  readonly Math = Math;

  readonly dataConsumptionState$: Observable<DataConsumptionState> = this.storageService
    .getActiveClientStorageSpace()
    .pipe(map((storageSpace) => this.dataConsumptionStateService.getDataConsumptionStateConfig(storageSpace)));

  readonly activeClientDataConsumption$: Observable<ClientDataConsumption> = this.store
    .select(selectClientCode)
    .pipe(switchMap((clientCode) => this.store.select(selectClientDataConsumptionInGb(clientCode))));

  manageStorage(): void {
    this.dialogService
      .openComponent(DataConsumptionComponent, {
        label: this.translocoService.translate('Gérer votre stockage'),
        data: { detailedMode: true },
      })
      .subscribe();
  }

  contact(): void {
    this.contactUsService.openPixacareSupportForm();
  }
}
